import React, { useMemo, useState } from 'react';
import { orderBy, limit } from 'firebase/firestore';
import { Card, Button, Badge } from '../../components/UI';
import { useFirestoreCollection, useFirestoreDocument } from '../../hooks';
import QueueCard from './QueueCard';
import OutreachCard from './OutreachCard';
import {
  toEur, formatEur, filterByTab, spendRatio, brusselsMonthKey, BANNERS,
  collectResultMessage, RUN_NOW_CONFIRM_MESSAGE, SETTINGS_DEFAULTS, outreachStateLabel,
} from './marketingFormat';
import {
  runMarketingNow, collectMarketingNow, approveMarketingItem, skipMarketingItem, updateMarketingSettings,
  editMarketingItem, neverContactMarketing, markMarketingTarget, retryMarketingSend,
  researchMarketingNow, sendMarketingNow,
} from '../../services/marketingService';
import styles from './Marketing.module.css';

const TABS = [
  { key: 'content', label: 'Content' },
  { key: 'outreach', label: 'Outreach' },
  { key: 'outbox', label: 'Outbox' },
  { key: 'flagged', label: 'Flagged' },
  { key: 'history', label: 'History' },
];

const Marketing = () => {
  const [tab, setTab] = useState('content');
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState(null);
  const [capDraft, setCapDraft] = useState('');
  const [limitDraft, setLimitDraft] = useState('');

  const constraints = useMemo(() => [orderBy('createdAt', 'desc'), limit(200)], []);
  const { data: items, loading, error } = useFirestoreCollection('marketingQueue', constraints);
  const { data: storedSettings } = useFirestoreDocument('marketingConfig', 'settings');
  const { data: status } = useFirestoreDocument('marketingConfig', 'status');
  const { data: spend } = useFirestoreDocument('marketingSpend', brusselsMonthKey());

  const settings = { ...SETTINGS_DEFAULTS, ...(storedSettings || {}) };
  const monthSpentUsd = spend?.totalUsd || 0;
  const monthEur = toEur(monthSpentUsd, settings.usdToEur);
  const visible = filterByTab(items, tab);
  const waiting = items.filter((i) => i.status === 'pending_approval').length;

  // Resolves true when the call succeeded, false when it failed (the error
  // is shown in the page banner), so a caller like the edit panel can stay
  // open on failure.
  const act = async (id, fn, ok) => {
    setBusyId(id);
    setMessage(null);
    try {
      const res = await fn();
      setMessage({ type: 'ok', text: ok(res) });
      return true;
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const saveCap = () => {
    const capEur = Number(capDraft);
    act('settings', () => updateMarketingSettings({ capEur }), () => `Cap set to ${formatEur(capEur)}`);
    setCapDraft('');
  };

  const saveLimit = () => {
    const dailyOutreachLimit = Number(limitDraft);
    act('settings', () => updateMarketingSettings({ dailyOutreachLimit }), () => `Daily outreach limit set to ${dailyOutreachLimit}`);
    setLimitDraft('');
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Marketing</h1>

      {status?.banner && BANNERS[status.banner] && <div className={styles.banner}>{BANNERS[status.banner]}</div>}
      {status?.lastRunStatus === 'failed' && <div className={styles.banner}>{BANNERS.run_failed}</div>}
      {message && <div className={message.type === 'ok' ? styles.ok : styles.banner}>{message.text}</div>}

      <Card padding="md">
        <div className={styles.topBar}>
          <div className={styles.spend}>
            <div className={styles.spendLabel}>
              This month {formatEur(monthEur)} / {formatEur(settings.capEur)}
            </div>
            <div className={styles.spendTrack}>
              <div className={styles.spendFill} style={{ width: `${spendRatio(monthEur, settings.capEur) * 100}%` }} />
            </div>
            {settings.creditsRemainingUsd !== null && (
              <div className={styles.muted}>Credits left: ${Number(settings.creditsRemainingUsd).toFixed(2)}</div>
            )}
          </div>
          <div className={styles.topMeta}>
            <Badge variant={settings.mode === 'live' ? 'danger' : 'warning'}>{settings.mode === 'live' ? 'Live — emails go to businesses' : 'Dry run'}</Badge>
            <Badge variant={settings.paused ? 'danger' : 'success'}>{settings.paused ? 'Paused' : 'Running'}</Badge>
            <Badge variant={settings.outreachPaused ? 'danger' : 'success'}>{outreachStateLabel(settings)}</Badge>
            <span className={styles.muted}>{waiting} waiting</span>
          </div>
          <div className={styles.topActions}>
            <input
              className={styles.capInput}
              type="number" min="1" max="500" placeholder="Cap €"
              value={capDraft} onChange={(e) => setCapDraft(e.target.value)}
            />
            <Button size="sm" variant="outline" disabled={!capDraft || busyId === 'settings'} onClick={saveCap}>Set cap</Button>
            <input
              className={styles.limitInput}
              type="number" min="0" max="20" placeholder="Limit/day"
              value={limitDraft} onChange={(e) => setLimitDraft(e.target.value)}
            />
            <Button size="sm" variant="outline" disabled={!limitDraft || busyId === 'settings'} onClick={saveLimit}>Set</Button>
            {settings.outreachPaused && (
              <Button size="sm" variant="primary" disabled={busyId === 'settings'}
                onClick={() => {
                  if (!window.confirm('Resume outreach? Make sure the bounce/complaint cause is fixed.')) return;
                  act('settings', () => updateMarketingSettings({ outreachPaused: false, outreachPauseReason: null }), () => 'Outreach resumed');
                }}>
                Resume outreach
              </Button>
            )}
            <Button
              size="sm" variant={settings.paused ? 'primary' : 'danger'} disabled={busyId === 'settings'}
              onClick={() => act('settings', () => updateMarketingSettings({ paused: !settings.paused }),
                () => (settings.paused ? 'Resumed' : 'Paused everything'))}
            >
              {settings.paused ? 'Resume' : 'Pause everything'}
            </Button>
            <Button size="sm" variant="secondary" disabled={busyId === 'run'}
              onClick={() => {
                if (!window.confirm(RUN_NOW_CONFIRM_MESSAGE)) return;
                act('run', runMarketingNow, (r) => `Run: ${r.status}${r.count ? ` (${r.count} drafts)` : ''}`);
              }}>
              Run now
            </Button>
            <Button size="sm" variant="secondary" disabled={busyId === 'collect'}
              onClick={() => act('collect', collectMarketingNow, collectResultMessage)}>
              Collect now
            </Button>
            <Button size="sm" variant="secondary" disabled={busyId === 'research'}
              onClick={() => act('research', researchMarketingNow,
                (r) => (r.skipped ? 'Skipped (paused)' : `Found ${r.found}, added ${r.added} (${r.ready} with an address)`))}>
              Find businesses
            </Button>
            <Button size="sm" variant="secondary" disabled={busyId === 'send'}
              onClick={() => act('send', sendMarketingNow,
                (r) => `Sent ${r.sent.length}, skipped ${r.skipped.length}, failed ${r.failed.length}${r.reason ? ` — ${r.reason}` : ''}`)}>
              Send queued now
            </Button>
          </div>
        </div>
      </Card>

      <div className={styles.tabs}>
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)}
            className={tab === t.key ? styles.tabActive : styles.tab}>
            {t.label} ({filterByTab(items, t.key).length})
          </button>
        ))}
      </div>

      {loading && <p className={styles.muted}>Loading…</p>}
      {error && <p className={styles.banner}>Could not load the queue: {error.message}</p>}
      {!loading && visible.length === 0 && <p className={styles.muted}>Nothing here.</p>}

      {visible.map((item) => (
        item.channel === 'outreach' ? (
          <OutreachCard
            key={item.id}
            item={item}
            monthSpentUsd={monthSpentUsd}
            settings={settings}
            busy={busyId === item.id}
            onApprove={(it) => act(it.id, () => approveMarketingItem(it.id), () => 'Approved')}
            onSkip={(it, reason) => act(it.id, () => skipMarketingItem(it.id, reason), () => `Skipped (${reason})`)}
            onEdit={(it, fields) => act(it.id, () => editMarketingItem(it.id, fields),
              (r) => (r.status === 'flagged' ? 'Saved — still flagged, see the problems' : 'Saved — ready to approve'))}
            onNeverContact={(it) => act(it.id, () => neverContactMarketing(it.id), () => 'Blocked — this business will never be contacted')}
            onMark={(it, o) => act(it.id, () => markMarketingTarget(it.id, o), () => `Marked ${o}`)}
            onRetry={(it) => act(it.id, () => retryMarketingSend(it.id), () => 'Queued to send again')}
          />
        ) : (
          <QueueCard
            key={item.id}
            item={item}
            monthSpentUsd={monthSpentUsd}
            settings={settings}
            busy={busyId === item.id}
            onApprove={(it) => act(it.id, () => approveMarketingItem(it.id), () => 'Approved')}
            onSkip={(it, reason) => act(it.id, () => skipMarketingItem(it.id, reason), () => `Skipped (${reason})`)}
            onEdit={(it, fields) => act(it.id, () => editMarketingItem(it.id, fields),
              (r) => (r.status === 'flagged' ? 'Saved — still flagged, see the problems' : 'Saved — ready to approve'))}
          />
        )
      ))}
    </div>
  );
};

export default Marketing;
